export interface Coordinates {
  lat: number;
  lng: number;
}

export type MapStyle =
  | "light"
  | "dark"
  | "satellite"
  | "topography"
  | "satellite-streets"
  | "navigation-day";

export const MAP_STYLES: Record<
  MapStyle,
  {
    name: string;
    url: string;
    description: string;
    color: string;
    thumbnail: string;
  }
> = {
  light: {
    name: "Light",
    description: "Clean backdrop for data visualization",
    url: "mapbox://styles/mapbox/light-v11",
    color: "#f3f4f6", // Light gray
    thumbnail: "/images/map-styles/light.jpg",
  },
  dark: {
    name: "Dark",
    description: "Dark theme for data visualization",
    url: "mapbox://styles/mapbox/dark-v11",
    color: "#1f2937", // Dark gray
    thumbnail: "/images/map-styles/dark.jpg",
  },
  satellite: {
    name: "Satellite",
    description: "Real-world satellite imagery",
    url: "mapbox://styles/mapbox/satellite-v9",
    color: "#2d5016", // Earth green
    thumbnail: "/images/map-styles/satellite.jpg",
  },
  topography: {
    name: "Topography",
    description: "Terrain and elevation details",
    url: "mapbox://styles/mapbox/outdoors-v12",
    color: "#4a7c59", // Mountain green
    thumbnail: "/images/map-styles/topography.jpg",
  },
  "satellite-streets": {
    name: "Satellite Streets",
    description: "Satellite with street labels",
    url: "mapbox://styles/mapbox/satellite-streets-v12",
    color: "#166534", // Dark green
    thumbnail: "/images/map-styles/satellite-streets.jpg",
  },
  "navigation-day": {
    name: "Navigation Day",
    description: "Optimized for daytime navigation",
    url: "mapbox://styles/mapbox/navigation-day-v1",
    color: "#2563eb", // Navigation blue
    thumbnail: "/images/map-styles/navigation-day.jpg",
  },
};

export interface State {
  id: string;
  name: string;
  coordinates: Coordinates[][];
  color: string;
  description?: string;
  population?: number;
  capital?: string;
  capitalCoordinates?: Coordinates;
  createdAt: string;
  isBorder?: boolean; // True if this is the region border layer
}

// Firestore-compatible version (flattened coordinates)
export interface FirestoreState {
  id: string;
  name: string;
  coordinates: Coordinates[]; // Flattened array for Firestore
  color: string;
  description?: string;
  population?: number;
  capital?: string;
  capitalCoordinates?: Coordinates; // Custom coordinates for capital pin
  createdAt: string;
}

// Firestore-compatible version of MapData
export interface FirestoreMapData {
  id: string;
  name: string;
  description?: string;
  states: FirestoreState[];
  guideLayers?: GuideLayer[];
  createdAt: string;
  updatedAt: string;
  country: string;
  region?: RegionCode; // Region this map belongs to (e.g., 'iran', 'iraq', 'syria')
  userId: string;
  userDisplayName: string;
  userAvatar?: string;
  isPublic: boolean;
  likesCount: number;
  commentsCount: number;
  viewsCount: number;
  tags?: string[];
  thumbnail?: string;
  // Approval-related fields
  approvalRequested?: boolean;
  approvalRequestedAt?: string;
  isApproved?: boolean; // New field: map is approved but may still be private
  approvedAt?: string;
  isRejected?: boolean;
  rejectionReason?: string;
  rejectedAt?: string;
  // Version control fields
  version?: number; // Current version number
  approvedVersion?: number; // Version that was approved (if any)
  // Custom border (stored as JSON string to avoid nested arrays)
  customBorderJson?: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  displayName: string;
  avatar?: string;
  bio?: string;
  createdAt: string;
  mapsCount: number;
  followersCount: number;
  followingCount: number;
  role: UserRole;
  isApproved: boolean;
  subscriptionTier: SubscriptionTier;
  emailVerified: boolean;
  emailVerificationSentAt?: string;
  usernameLastChangedAt?: string;
  previousUsernames?: string[];
}

export type UserRole = "admin" | "user" | "approver" | "public";

export type SubscriptionTier = "free" | "premium" | "enterprise";

export interface UserLimits {
  maxMaps: number;
  maxOverlays: number;
  canCreatePublicMaps: boolean;
  canApproveMaps: boolean;
  canAccessAdminPanel: boolean;
}

export interface Comment {
  id: string;
  mapId: string;
  userId: string;
  userDisplayName: string;
  userAvatar?: string;
  content: string;
  createdAt: string;
}

export interface Like {
  id: string;
  mapId: string;
  userId: string;
  createdAt: string;
}

export interface MapData {
  id: string;
  name: string;
  description?: string;
  states: State[];
  guideLayers?: GuideLayer[];
  customBorder?: Coordinates[][]; // Custom region border (separate from states)
  createdAt: string;
  updatedAt: string;
  country: string;
  region?: RegionCode; // Region this map belongs to (e.g., 'iran', 'iraq', 'syria')
  userId: string;
  userDisplayName: string;
  userAvatar?: string;
  isPublic: boolean;
  likesCount: number;
  commentsCount: number;
  viewsCount: number;
  tags?: string[];
  thumbnail?: string;
  // Approval-related fields
  approvalRequested?: boolean;
  approvalRequestedAt?: string;
  isApproved?: boolean; // New field: map is approved but may still be private
  approvedAt?: string;
  isRejected?: boolean;
  rejectionReason?: string;
  rejectedAt?: string;
  // Version control fields
  version?: number; // Current version number
  approvedVersion?: number; // Version that was approved (if any)
}

// Region code type - exported for use across the app
export type RegionCode =
  | "iran"
  | "iraq"
  | "syria"
  | "kurdistan"
  | "baluchistan"
  | "afghanistan"
  | "turkey"
  | "armenia"
  | "azerbaijan"
  | "turkmenistan"
  | "pakistan"
  | "custom";

// Map Version History - stored in subcollection
export interface MapVersion {
  id: string;
  mapId: string;
  version: number;
  // Snapshot of map data at this version
  name: string;
  description?: string;
  states: State[];
  guideLayers?: GuideLayer[];
  // Metadata
  createdAt: string;
  createdBy: string; // userId
  createdByDisplayName: string;
  changeDescription?: string; // Optional description of what changed
  // Approval status at this version
  isApproved?: boolean;
  approvedAt?: string;
  approvedBy?: string;
  // Thumbnail at this version
  thumbnail?: string;
}

export interface MapComponentRef {
  captureMap: () => Promise<HTMLCanvasElement>;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export interface SocialState {
  maps: MapData[];
  currentMap: MapData | null;
  comments: Comment[];
  likes: Like[];
  loading: boolean;
  error: string | null;
}

export interface GuideLayer {
  id: string;
  name: string;
  description?: string;
  imageUrl: string;
  bounds: Coordinates[]; // 4 corners: SW, SE, NE, NW
  opacity: number;
  visible: boolean;
  // Image transformation properties
  rotation: number; // Rotation in degrees
  scale: number; // Scale factor (1.0 = original size)
  offsetX: number; // Horizontal offset in pixels
  offsetY: number; // Vertical offset in pixels
  // Advanced positioning
  centerLat: number; // Center latitude
  centerLng: number; // Center longitude
  width: number; // Width in degrees
  height: number; // Height in degrees
  createdAt: string;
  updatedAt: string;
}
